// see docs/EXTRACTED_COMMENTS.md#c194

// see docs/EXTRACTED_COMMENTS.md#c733
export const TEXT_MARKUP_TAGS = [
    "br", "ul", "ol", "li", "strong", "em", "small",
] as const;

export function sanitizeHtml(dirty: string, allowedTags?: readonly string[]): string {
    const escaped = String(dirty)
        .replace(/&/g, "&amp;")   // must run first so later entities aren't double-escaped
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    if (!allowedTags || allowedTags.length === 0) return escaped;
    const bareTag = allowedTagRegex(allowedTags);
    if (!bareTag) return escaped;
    return escaped.replace(bareTag, (_match, close: string, name: string, selfClose: string) =>
        `<${close}${name.toLowerCase()}${selfClose}>`);
}

// Built once per tag list (callers pass a shared constant like TEXT_MARKUP_TAGS);
// String.replace resets a global regex's lastIndex, so sharing it is safe.
const allowedTagRegexes = new WeakMap<readonly string[], RegExp | null>();

function allowedTagRegex(allowedTags: readonly string[]): RegExp | null {
    let re = allowedTagRegexes.get(allowedTags);
    if (re === undefined) {
        const names = allowedTags
            .map(tag => String(tag).toLowerCase().replace(/[^a-z0-9]/g, ""))
            .filter(Boolean);
        // see docs/EXTRACTED_COMMENTS.md#c734
        re = names.length === 0 ? null : new RegExp(`&lt;(/?)(${names.join("|")})\\s*(/?)&gt;`, "gi");
        allowedTagRegexes.set(allowedTags, re);
    }
    return re;
}

// see docs/EXTRACTED_COMMENTS.md#c195
const FORMAT_TOKEN_SOURCE =
    "(%(\\d+\\$)?([+-]?)(')?(\\d+)?(\\.\\d+)?([bcdeEufFgGosxX]|ld))" +
    "|( hh:mm:ss|HH:mm:ss|h:mm a |hh:mm a|%H:%M|HH:MM|HH:mm|hh:mm|h:mm" +
    "| hhmmss|HHmmss|hmm a |hhmm a|%H%M|HHMM|HHmm|hhmm|hmm)";
const formatTokenRegex = () => new RegExp(FORMAT_TOKEN_SOURCE, "g");

// Constructing an Intl.DateTimeFormat costs far more than formatting with one, and
// formatTime needs up to nine per call (once per date field per data update), so the
// instances are cached per (part, locale, timeZone). Formatting output is unchanged.
const DATE_PART_OPTIONS = {
    weekdayLong: { weekday: 'long' },
    day2: { day: '2-digit' },
    monthLong: { month: 'long' },
    monthShort: { month: 'short' },
    yearNumeric: { year: 'numeric' },
    month2: { month: '2-digit' },
    monthNumeric: { month: 'numeric' },
    dayNumeric: { day: 'numeric' },
    time24: { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
} satisfies Record<string, Intl.DateTimeFormatOptions>;
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

function dateTimeFormat(part: keyof typeof DATE_PART_OPTIONS, locale: string, timeZone: string | undefined): Intl.DateTimeFormat {
    const key = `${part}\0${locale}\0${timeZone ?? ""}`;
    let dtf = dateTimeFormats.get(key);
    if (!dtf) {
        dtf = new Intl.DateTimeFormat(locale, { ...DATE_PART_OPTIONS[part], timeZone });
        dateTimeFormats.set(key, dtf);
    }
    return dtf;
}

export const formatTime = (currentDate: Date, format: string, field_type: number = 2, locale: string = "en", opts?: { timeZone?: string, offsetMinutes?: number }) => {
    const validDate = currentDate instanceof Date ? currentDate : new Date(currentDate);
    if (!(validDate instanceof Date) || isNaN(validDate.getTime())) {
        return format;
    }


    const offsetMinutes = Number.isFinite(opts?.offsetMinutes) ? Number(opts!.offsetMinutes) : 0;
    const timeZone = opts?.timeZone;
    const baseDate = offsetMinutes === 0 ? new Date(validDate) : new Date(validDate.getTime() + offsetMinutes * 60000);

    // Check if the format includes a time token (HH / hh / H / mm)
    const hasTime = /HH|hh|H|mm/.test(format);

    // Locale-aware day and month names (respect timeZone when provided)
    const dayName = dateTimeFormat('weekdayLong', locale, timeZone).format(baseDate); // 'dddd'
    const day = String(dateTimeFormat('day2', locale, timeZone).format(baseDate)).padStart(2, '0'); // 'dd'
    const monthName = dateTimeFormat('monthLong', locale, timeZone).format(baseDate); // 'MMMM'
    const monthNameShort = dateTimeFormat('monthShort', locale, timeZone).format(baseDate); // 'MMM'
    const yearNumber = Number(dateTimeFormat('yearNumeric', locale, timeZone).format(baseDate));
    const year = isNaN(yearNumber) ? baseDate.getFullYear() : yearNumber; // 'yyyy'
    const shortYear = year % 2000; // 'yy'
    const month = dateTimeFormat('month2', locale, timeZone).format(baseDate); // 'MM'
    const shortMonth = dateTimeFormat('monthNumeric', locale, timeZone).format(baseDate); // 'M'
    const shortDay = dateTimeFormat('dayNumeric', locale, timeZone).format(baseDate); // 'd'


    // TODO: look into %s a bit more ig
    switch (field_type) {
        case 4: {
            if (format == "%s") {
                return `${year}${month}${day}`
            }
        }
    }

    if (format == "%s") return String(currentDate);

    // see docs/EXTRACTED_COMMENTS.md#c577
    const tokenMap: Record<string, string> = {
        dddd: dayName,
        MMMM: monthName,
        yyyy: String(year),
        MMM: monthNameShort,
        dd: day,
        MM: month,
        yy: String(shortYear),
        d: shortDay,
        M: shortMonth,
    };

    if (hasTime) {
        const timeParts = timeZone
            ? dateTimeFormat('time24', locale, timeZone).formatToParts(baseDate)
            : null;

        tokenMap.HH = timeParts?.find(p => p.type === 'hour')?.value
            ?? String(baseDate.getHours()).padStart(2, '0'); // 'HH'
        tokenMap.hh = tokenMap.HH; // 12-hour spelling kept as 24-hour value (legacy)
        tokenMap.H = String(Number(tokenMap.HH)); // 'H' — unpadded 24-hour hour
        tokenMap.ss = timeParts?.find(p => p.type === 'second')?.value
            ?? String(baseDate.getSeconds()).padStart(2, '0'); // 'ss'
        tokenMap.mm = timeParts?.find(p => p.type === 'minute')?.value
            ?? String(baseDate.getMinutes()).padStart(2, '0'); // 'mm'
    }

    // see docs/EXTRACTED_COMMENTS.md#c578
    const orderedTokens = Object.keys(tokenMap).sort((a, b) => b.length - a.length);

    let formattedDate = "";
    let idx = 0;
    scan: while (idx < format.length) {
        for (const tok of orderedTokens) {
            if (format.startsWith(tok, idx)) {
                formattedDate += tokenMap[tok];
                idx += tok.length;
                continue scan;
            }
        }
        formattedDate += format[idx];
        idx += 1;
    }

    // Return formatted date
    return formattedDate;
}

export function sprintf(format: string, value: any, field_type: number = -1): string {
    if (!format) {
        return "";
    }

    let is_date = false;

    // Value manipulation in order to match what is required.
    switch (field_type) {
        // Number
        case 1: {
            // value = parseInt(value);
            break;
        }
        // String
        case 2: {
            value = String(value);
            break;
        }
        // Date Time (Epooch based)
        case 3: {
            value = new Date(value * 1000);
            is_date = true;
            break;
        }
        // UI Date
        case 4: {
            // Implemented from Formatter:107
            value = new Date(Math.floor(value / 10000), ((Math.floor(value / 100) - 1) % 100), Math.floor(value % 100), 0, 0);
            is_date = true;
            break;
        }
        // UI Time
        case 5: {
            // Implemented from Formatter:131
            value = new Date(1970, 1, 1, Math.floor(value / 100), Math.floor(value % 100));
            break;
        }
        // Boolean
        case 6: {
            value = Number(`${value}` == "true")
            break;
        }
    }

    if (is_date) {
        return formatTime(value, format, field_type);
    }

    let matches = format.match(formatTokenRegex());
    if (matches?.length == 0) {
        return format;
    }

    return format.replace(formatTokenRegex(), (_, _2, _3, sign, padding, minWidth, precision, type, type2) => {
        if (value == undefined) {
            value = ""
        }

        switch (type) {
            case 's': // String
                value = String(value);
                if (precision) value = value.slice(0, parseInt(precision.slice(1)));
                break;
            case 'd':
            case 'i': // Integer
                value = parseInt(value, 10).toString();
                break;
            case 'f': // Floating-point number
                value = parseFloat(value).toFixed(precision ? parseInt(precision.slice(1)) : 6);
                break;
            case 'x': // Hexadecimal (lowercase)
                value = parseInt(value, 10).toString(16);
                break;
            case 'X': // Hexadecimal (uppercase)
                value = parseInt(value, 10).toString(16).toUpperCase();
                break;
        }

        if (type2 != undefined) {
            value = formatTime(new Date(value), type2);
        }


        // Apply minimum width and padding
        if (minWidth) {
            const width = parseInt(minWidth, 10);
            const padChar = padding === "'" ? padding : ' ';
            if (value.length < width) {
                value = (sign === '-' ? value.padEnd(width, padChar) : value.padStart(width, padChar));
            }
        }

        return value;
    });
}

export function parsef(format: string, input: string, field_type: number = -1): any {
    if (!format) {
        return input;
    }

    const tokenRegex = formatTokenRegex();

    const tokens: Array<{
        raw: string;
        type?: string;
        timeType?: string;
    }> = [];

    let pattern = "^";
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRegex.exec(format)) !== null) {
        const [raw, _full, _argIndex, _sign, _padding, _minWidth, _precision, type, timeType] = match;
        const index = match.index;

        // Escape literal text between tokens
        pattern += escapeRegex(format.slice(lastIndex, index));

        if (timeType !== undefined) {
            pattern += buildDatePattern(timeType);
            tokens.push({ raw, timeType });
        } else {
            pattern += buildValuePattern(type);
            tokens.push({ raw, type });
        }

        lastIndex = index + raw.length;
    }

    pattern += escapeRegex(format.slice(lastIndex));
    pattern += "$";

    const regex = new RegExp(pattern);
    const result = regex.exec(input);

    if (!result) {
        return null;
    }

    // see docs/EXTRACTED_COMMENTS.md#c579
    let captureIndex = 1;

    for (const token of tokens) {
        if (token.timeType !== undefined) {
            const value = parseDateToken(token.timeType, result, captureIndex);

            if (!value) {
                return null;
            }

            return coerceParsedDate(value, field_type);
        }

        if (token.type !== undefined) {
            const rawValue = result[captureIndex++];
            return coerceParsedValue(rawValue, token.type, field_type);
        }
    }

    return input;
}

function coerceParsedValue(rawValue: string, type: string, field_type: number): any {
    // field_type takes priority when explicitly provided
    switch (field_type) {
        case 1:
            return Number(rawValue);
        case 2:
            return String(rawValue);
        case 3:
            return coerceDateLikeRawValue(rawValue, 3);
        case 4:
            return coerceDateLikeRawValue(rawValue, 4);
        case 6:
            return rawValue === "1" || rawValue.toLowerCase() === "true";
    }


    switch (type) {
        case "d":
        case "u":
        case "ld":
            return parseInt(rawValue, 10);

        case "f":
        case "F":
        case "e":
        case "E":
        case "g":
        case "G":
            return parseFloat(rawValue);

        case "x":
        case "X":
            return parseInt(rawValue, 16);

        case "b":
            return parseInt(rawValue, 2);

        case "o":
            return parseInt(rawValue, 8);

        case "c":
        case "s":
        default:
            return rawValue;
    }
}

function coerceDateLikeRawValue(rawValue: string, field_type: 3 | 4): number {
    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue)) {
        return Math.trunc(numericValue);
    }

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return 0;
    }

    if (field_type === 3) {
        return Math.floor(parsedDate.getTime() / 1000);
    }

    return (
        parsedDate.getFullYear() * 10000 +
        (parsedDate.getMonth() + 1) * 100 +
        parsedDate.getDate()
    );
}

function coerceParsedDate(date: Date, field_type: number): any {
    switch (field_type) {
        // Date Time (Epoch based)
        case 3:
            return Math.floor(date.getTime() / 1000);

        // UI Date -> YYYYMMDD
        case 4:
            return (
                date.getFullYear() * 10000 +
                (date.getMonth() + 1) * 100 +
                date.getDate()
            );

        // UI Time -> HHMM
        case 5:
            return date.getHours() * 100 + date.getMinutes();

        default:
            return date;
    }
}

function buildValuePattern(type: string): string {
    switch (type) {
        case "d":
        case "u":
        case "ld":
            return "([+-]?\\d+)";

        case "f":
        case "F":
        case "e":
        case "E":
        case "g":
        case "G":
            return "([+-]?\\d+(?:\\.\\d+)?)";

        case "x":
        case "X":
            return "([0-9a-fA-F]+)";

        case "b":
            return "([01]+)";

        case "o":
            return "([0-7]+)";

        case "c":
            return "(.)";

        case "s":
        default:
            return "(.+?)";
    }
}

// ':' is optional on both sides: "HHmm" is a token, and it matches "0930" as well as "09:30"
const stripTimeSeparators = (format: string): string => format.replace(/:/g, "");

function buildDatePattern(format: string): string {
    switch (stripTimeSeparators(format)) {
        case "HHMM":
        case "HHmm":
        case "%H%M":
            return "(\\d{2}):?(\\d{2})";

        case "hhmm":
        case "hmm":
            return "(\\d{1,2}):?(\\d{2})";

        case "HHmmss":
            return "(\\d{2}):?(\\d{2}):?(\\d{2})";

        case "hhmm a":
        case "hmm a ":
            return "(\\d{1,2}):?(\\d{2})\\s*([aApP][mM])";

        case " hhmmss":
            return "\\s*(\\d{1,2}):?(\\d{2}):?(\\d{2})";

        default:
            return "(.+?)";
    }
}

function parseDateToken(format: string, match: RegExpExecArray, captureIndex: number): Date | null {
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    switch (stripTimeSeparators(format)) {
        case "HHMM":
        case "HHmm":
        case "%H%M": {
            hours = parseInt(match[captureIndex], 10);
            minutes = parseInt(match[captureIndex + 1], 10);
            return new Date(1970, 0, 1, hours, minutes, 0);
        }

        case "hhmm":
        case "hmm": {
            hours = parseInt(match[captureIndex], 10);
            minutes = parseInt(match[captureIndex + 1], 10);
            return new Date(1970, 0, 1, hours, minutes, 0);
        }

        case "HHmmss": {
            hours = parseInt(match[captureIndex], 10);
            minutes = parseInt(match[captureIndex + 1], 10);
            seconds = parseInt(match[captureIndex + 2], 10);
            return new Date(1970, 0, 1, hours, minutes, seconds);
        }

        case "hhmm a":
        case "hmm a ": {
            hours = parseInt(match[captureIndex], 10);
            minutes = parseInt(match[captureIndex + 1], 10);
            const ampm = match[captureIndex + 2].toLowerCase();

            if (ampm === "pm" && hours < 12) hours += 12;
            if (ampm === "am" && hours === 12) hours = 0;

            return new Date(1970, 0, 1, hours, minutes, 0);
        }

        case " hhmmss": {
            hours = parseInt(match[captureIndex], 10);
            minutes = parseInt(match[captureIndex + 1], 10);
            seconds = parseInt(match[captureIndex + 2], 10);
            return new Date(1970, 0, 1, hours, minutes, seconds);
        }

        default:
            return null;
    }
}

function escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function parseUserAgent(uaString = navigator.userAgent) {
    const ua = uaString.toLowerCase();

    // Detect browser + version
    let browser = "Unknown";
    let version = "Unknown";
    const browserRegexes = [
        { name: "Edge", regex: /(edg)\/([\d\.]+)/ },
        { name: "Chrome", regex: /(chrome)\/([\d\.]+)/ },
        // Samsung Tizen engine: "(KHTML, like Gecko) 85.0.4183.93/6.5 TV Safari" — Chromium without a Chrome/ token.
        { name: "Chromium", regex: /(gecko\)) ([\d\.]+)\/[\d\.]+ tv safari/ },
        { name: "Safari", regex: /(version)\/([\d\.]+).*safari/ },
        { name: "Firefox", regex: /(firefox)\/([\d\.]+)/ },
        { name: "Opera", regex: /(opr|opera)\/([\d\.]+)/ },
        { name: "Internet Explorer", regex: /(msie|trident)\/? ?([\d\.]+)/ }
    ];

    for (let b of browserRegexes) {
        const match = ua.match(b.regex);
        if (match) {
            browser = b.name;
            version = match[2];
            break;
        }
    }

    // Detect OS
    let os = "Unknown";
    if (ua.includes("windows nt 10")) os = "Windows 10";
    else if (ua.includes("windows nt 6.3")) os = "Windows 8.1";
    else if (ua.includes("windows nt 6.2")) os = "Windows 8";
    else if (ua.includes("windows nt 6.1")) os = "Windows 7";
    else if (ua.includes("tizen")) os = "Tizen";
    else if (ua.includes("mac os x")) os = "macOS";
    else if (ua.includes("android")) os = "Android";
    else if (ua.includes("iphone") || ua.includes("ipad")) os = "iOS";
    else if (ua.includes("linux")) os = "Linux";

    // see docs/EXTRACTED_COMMENTS.md#c196
    let osName = "";
    let osVersion = "";
    if (os.startsWith("Windows")) {
        osName = "Windows";
        osVersion = os.slice("Windows ".length); // "10" | "8.1" | "8" | "7"
    } else if (os === "macOS") {
        osName = "macOS";
        const m = ua.match(/mac os x ([\d_]+)/);
        if (m) osVersion = m[1].replace(/_/g, ".");
    } else if (os === "Android") {
        osName = "Android";
        const m = ua.match(/android ([\d.]+)/);
        if (m) osVersion = m[1];
    } else if (os === "iOS") {
        osName = "iOS";
        const m = ua.match(/os ([\d_]+)/);
        if (m) osVersion = m[1].replace(/_/g, ".");
    } else if (os === "Tizen") {
        osName = "Tizen";
        const m = ua.match(/tizen ([\d.]+)/);
        if (m) osVersion = m[1];
    } else if (os === "Linux") {
        osName = "Linux";
    }

    // Detect device type
    let device = "Desktop";
    if (/mobile|iphone|android/i.test(ua)) {
        device = "Mobile";
    } else if (/ipad|tablet/i.test(ua)) {
        device = "Tablet";
    }

    // Hostname (client side only)
    const hostname = window?.location?.hostname || "Unknown";

    return { browser, version, os, osName, osVersion, device, hostname };
}

export function convertStyle(style: string | number): string {
    switch (style) {
        case 2: return "normal";
        case 3: return "italic";
        default: return "normal"
    }
}

export function convertWeight(weight: string | number): string {
    switch (weight) {
        case 2: return "normal";
        case 3: return "bold";
        default: return "normal"
    }
}
